import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  useTheme,
  Avatar,
  Chip,
  Divider,
  Grid,
  Alert,
  IconButton,
  Modal,
  Snackbar,
  SnackbarContent,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  School,
  PictureAsPdf,
  Star,
  Person,
  ShareOutlined,
  Close as CloseIcon,
  Link as CopyLinkIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
  ContactMail,
  AccessTime,
  WorkspacePremium,
  Language as LanguageIcon,
  LocalOffer,
  VerifiedUser,
} from '@mui/icons-material';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';
import { BusinessCourse } from '@/constants/types';
import { businessCourseService } from '@/services/business/businessCourseService';
import { courseLeadService } from '@/services/business/courseLeadService';
import { getFileUrl } from '@/utils/businessHelpers';
import { slateJsonToHtml } from '@/utils/slateSerializer';
import { ContentWithReadMore } from '@/components/dashboard/feed/ContentWithReadMore';
import toast from 'react-hot-toast';

interface PopulatedBusinessInfo {
  _id: string;
  businessName?: string;
  logoId?: string;
  email?: string;
  contactNumber?: string;
  whatsappNumber?: string;
}

interface PopulatedUserInfo {
  _id: string;
  name?: string;
  email?: string;
  profileImage?: string;
}

export const BusinessCourseDetailPage: React.FC = () => {
  const { businessId, courseId } = useParams<{ businessId?: string; courseId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [course, setCourse] = useState<BusinessCourse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isEnquiring, setIsEnquiring] = useState(false);
  const [enquired, setEnquired] = useState(false);
  const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await businessCourseService.getBusinessCourse(courseId!);
      setCourse(response.data);
    } catch (err: any) {
      console.error('Failed to load course:', err);
      setError(err?.message || 'Failed to load course details');
    } finally {
      setIsLoading(false);
    }
  };

  const trackEnquiry = async () => {
    if (!course?._id) return;
    try {
      await courseLeadService.track(course._id);
      setEnquired(true);
    } catch {
      // surfaced via apiClient
    }
  };

  const handleEnquire = async () => {
    if (!course?._id || isEnquiring || enquired) return;
    try {
      setIsEnquiring(true);
      await trackEnquiry();
      toast.success('Enquiry sent. The business will reach out to you shortly.');
    } finally {
      setIsEnquiring(false);
    }
  };

  const handleWhatsAppEnquire = () => {
    const number = (course?.businessId as PopulatedBusinessInfo | undefined)?.whatsappNumber;
    if (!number) {
      toast.error('WhatsApp number not available');
      return;
    }
    const message = `Hi, I'm interested in the "${course?.courseTitle}" course. Please share more details.`;
    window.open(`https://wa.me/91${number}?text=${encodeURIComponent(message)}`, '_blank');
    trackEnquiry();
  };

  const handleDownloadBrochure = () => {
    const url = course?.brochure?.url;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
    trackEnquiry();
  };

  const getBusinessInfo = (): PopulatedBusinessInfo | null => {
    if (!course?.businessId) return null;
    if (typeof course.businessId === 'string') return { _id: course.businessId };
    return course.businessId as PopulatedBusinessInfo;
  };

  const getUserInfo = (): PopulatedUserInfo | null => {
    if (!course?.userId) return null;
    if (typeof course.userId === 'string') return { _id: course.userId };
    return course.userId as PopulatedUserInfo;
  };

  const businessInfo = getBusinessInfo();
  const userInfo = getUserInfo();

  const shareUrl = course ? `https://hrtalky.in/courses/${course._id}` : '';
  const shareTitle = course?.courseTitle || 'Check out this course';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbarOpen(true);
      setShareOpen(false);
    });
  };

  const socialIcons = [
    { icon: <CopyLinkIcon />, label: 'Copy link', onClick: handleCopyLink },
    { component: FacebookShareButton, icon: <FacebookIcon />, label: 'Facebook', props: { hashtag: '#HRTalky' } },
    { component: TwitterShareButton, icon: <TwitterIcon />, label: 'X', props: { title: shareTitle, hashtags: ['HRTalky', 'Course'] } },
    { component: LinkedinShareButton, icon: <LinkedInIcon />, label: 'LinkedIn', props: { title: shareTitle, source: 'HRTalky' } },
    { component: WhatsappShareButton, icon: <WhatsAppIcon />, label: 'WhatsApp', props: { title: `${shareTitle}\n\n${shareUrl}`, separator: '' } },
  ];

  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Skeleton width={100} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3, mb: 3 }} />
        <Skeleton width="60%" height={40} sx={{ mb: 1 }} />
        <Skeleton width="40%" height={24} sx={{ mb: 3 }} />
        <Skeleton height={20} sx={{ mb: 1 }} />
        <Skeleton height={20} sx={{ mb: 1 }} />
        <Skeleton width="80%" height={20} />
      </Box>
    );
  }

  if (error || !course) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2, textTransform: 'none' }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Course not found'}</Alert>
      </Box>
    );
  }

  const isPaid = course.courseType === 'Paid';
  const hasDiscount = isPaid && (course.discount || 0) > 0 && (course.price || 0) > 0;
  const finalPrice = course.finalPrice ?? (hasDiscount
    ? (course.price as number) - ((course.price as number) * (course.discount as number)) / 100
    : course.price ?? 0);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Back & Share */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => {
            if (businessId) {
              navigate(`/businesses/${businessId}`, { state: { activeTab: 2 } });
            } else if (businessInfo?._id) {
              navigate(`/businesses/${businessInfo._id}`, { state: { activeTab: 2 } });
            } else {
              navigate(-1);
            }
          }}
          sx={{ textTransform: 'none' }}
        >
          Back to Courses
        </Button>
        <IconButton onClick={() => setShareOpen(true)} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <ShareOutlined fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          overflow: 'hidden',
        }}
      >
        <Grid container>
          {/* Left Column - Thumbnail */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 280, sm: 360, md: '100%' },
                minHeight: { md: 520 },
                bgcolor: 'background.paper',
                borderRight: { md: `1px solid ${theme.palette.divider}` },
                borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
                overflow: 'hidden',
                '&:hover img': {
                  transform: 'scale(1.03)',
                },
              }}
            >
              {course.courseThumbnail?.url ? (
                <Box
                  component="img"
                  src={course.courseThumbnail.url}
                  alt={course.courseTitle}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}11 0%, ${theme.palette.primary.main}22 100%)`,
                  }}
                >
                  <School sx={{ fontSize: 120, color: 'text.disabled', opacity: 0.5 }} />
                </Box>
              )}

              {(course.isCurrentlyPromoted || course.isPromoted) && (
                <Chip
                  icon={<Star sx={{ fontSize: 14 }} />}
                  label="Promoted"
                  size="small"
                  color="warning"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    boxShadow: theme.shadows[4],
                  }}
                />
              )}
            </Box>
          </Grid>

          {/* Right Column - Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ p: { xs: 3, sm: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Top chips */}
              <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<School sx={{ fontSize: 14 }} />}
                  label={course.level}
                  size="small"
                  color="primary"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                  }}
                />
                <Chip
                  label={course.courseType}
                  size="small"
                  color={isPaid ? 'secondary' : 'success'}
                  sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
                />
                {course.certificationAvailable && (
                  <Chip
                    icon={<WorkspacePremium sx={{ fontSize: 14 }} />}
                    label="Certificate"
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                )}
                {course.language && (
                  <Chip
                    icon={<LanguageIcon sx={{ fontSize: 14 }} />}
                    label={course.language}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                  />
                )}
              </Box>

              {/* Title */}
              <Typography
                variant="h3"
                sx={{
                  mb: 2,
                  fontWeight: 800,
                  letterSpacing: -1,
                  lineHeight: 1.1,
                  fontSize: { xs: '1.75rem', sm: '2.25rem' },
                }}
              >
                {course.courseTitle}
              </Typography>

              {/* Price */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap', mb: 3 }}>
                {isPaid ? (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      ₹
                    </Typography>
                    <Typography
                      variant="h3"
                      color="primary"
                      sx={{ fontWeight: 800, letterSpacing: -1, lineHeight: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}
                    >
                      {Number(finalPrice).toLocaleString('en-IN')}
                    </Typography>
                    {hasDiscount && (
                      <>
                        <Typography
                          variant="body1"
                          color="text.disabled"
                          sx={{ textDecoration: 'line-through' }}
                        >
                          ₹{(course.price as number).toLocaleString('en-IN')}
                        </Typography>
                        <Chip
                          icon={<LocalOffer sx={{ fontSize: 12 }} />}
                          label={`${course.discount}% OFF`}
                          size="small"
                          color="error"
                          sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 800, letterSpacing: -1, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                    Free
                  </Typography>
                )}
              </Box>

              {/* Course meta facts */}
              <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{ mb: 3, color: 'text.secondary' }}>
                {course.duration && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <AccessTime sx={{ fontSize: 16 }} />
                    <Typography variant="body2" fontWeight={600}>{course.duration}</Typography>
                  </Box>
                )}
                {course.numberOfModules ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <School sx={{ fontSize: 16 }} />
                    <Typography variant="body2" fontWeight={600}>{course.numberOfModules} modules</Typography>
                  </Box>
                ) : null}
                {course.accessType && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <VerifiedUser sx={{ fontSize: 16 }} />
                    <Typography variant="body2" fontWeight={600}>{course.accessType}</Typography>
                  </Box>
                )}
              </Stack>

              {/* Hashtags */}
              {course.hashTags && course.hashTags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                  {course.hashTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={`#${tag}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  ))}
                </Box>
              )}

              {/* CTAs */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                {businessInfo?.whatsappNumber && (
                  <Button
                    onClick={handleWhatsAppEnquire}
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      py: 1.1,
                      borderRadius: 2,
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#1ebe57' },
                    }}
                  >
                    WhatsApp Enquire
                  </Button>
                )}
                {course.brochure?.url && (
                  <Button
                    onClick={handleDownloadBrochure}
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    sx={{ textTransform: 'none', fontWeight: 600, px: 2.5, py: 1.1, borderRadius: 2 }}
                  >
                    Download Brochure
                  </Button>
                )}
              </Box>

              <Box sx={{ flexGrow: 1 }} />

              <Button
                onClick={handleEnquire}
                disabled={isEnquiring || enquired}
                variant="contained"
                startIcon={enquired ? <CheckIcon /> : <ContactMail />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                  letterSpacing: 0.3,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: theme.shadows[4] },
                }}
              >
                {enquired ? 'Enquiry Sent' : isEnquiring ? 'Sending…' : 'Enquire Now'}
              </Button>

              {/* Listed by */}
              {userInfo && userInfo.name && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      src={userInfo.profileImage ? getFileUrl(userInfo.profileImage) : undefined}
                      sx={{ width: 32, height: 32 }}
                    >
                      {userInfo.name?.charAt(0).toUpperCase() || <Person />}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem', fontWeight: 600, lineHeight: 1.2 }}
                      >
                        Listed by
                      </Typography>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {userInfo.name}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Description Section */}
      {course.description && (
        <Box
          sx={{
            mt: 3,
            p: { xs: 2.5, sm: 3 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 2, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2 }}
          >
            About this course
          </Typography>
          <ContentWithReadMore
            content={slateJsonToHtml(course.description)}
            feedId={course._id}
            expandedContent={expandedContent}
            setExpandedContent={setExpandedContent}
          />
        </Box>
      )}

      {/* Share Modal */}
      <Modal open={shareOpen} onClose={() => setShareOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 340, bgcolor: 'background.paper',
          boxShadow: 24, p: 3, borderRadius: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Share Course</Typography>
            <IconButton size="small" onClick={() => setShareOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box>
            {socialIcons.map((social, i) => {
              const rowSx = {
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1, borderRadius: 2, mb: 0.5,
                cursor: 'pointer', width: '100%',
                '&:hover': { bgcolor: 'action.hover' },
              };
              if (social.onClick) {
                return (
                  <Box key={i} onClick={social.onClick} sx={rowSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </Box>
                );
              }
              const ShareBtn = social.component!;
              return (
                <Box key={i} sx={{ ...rowSx, p: 0 }}>
                  <ShareBtn url={shareUrl} {...(social.props || {})} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', width: '100%', color: 'inherit', textDecoration: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </ShareBtn>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Modal>

      {/* Copy snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <SnackbarContent
          sx={{ bgcolor: 'success.main', color: 'white', borderRadius: 2 }}
          message={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckIcon /><Typography variant="body2" sx={{ color: 'white' }}>Link copied!</Typography></Box>}
          action={<IconButton onClick={() => setSnackbarOpen(false)} color="inherit" size="small"><CloseIcon fontSize="small" /></IconButton>}
        />
      </Snackbar>
    </Box>
  );
};
